// src/pages/Verify.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { App } from '@capacitor/app';

import { useAuth } from '../context/AuthContext';
import fetchWithAuth from '../utils/fetchWithAuth';
import { Button } from '../components/ui/atoms/Button/Button';
import { apiConfig } from '../config.js';

type Status = 'sent' | 'pending' | 'success' | 'error' | 'resent';

export default function Verify() {
  const [status, setStatus] = useState<Status>('pending');
  const [busy, setBusy] = useState(false);
  const [retrySeconds, setRetrySeconds] = useState<number | null>(null);

  const { backendUser, loading, refreshBackendUser } = useAuth();
  const { search } = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(search);
  const token = params.get('token');
  const customTokenParam = params.get('customToken');
  console.log(`[Verify] 🏷  URL params → token=${token}, customTokenParam=${customTokenParam}`);
  const [webCustomToken, setWebCustomToken] = useState<string | null>(null);

  // Guards to ensure one-time actions
  const sentEmail = useRef(false);

  // 1) Handle customToken deep-links: mint token, sign into Firebase, refresh & go to onboarding
  useEffect(() => {
    const handleDeepLink = async (data: any) => {
      console.log('[Verify] Deep link received →', data.url);
      const url = new URL(data.url);
      const deepToken = url.searchParams.get('customToken');
      console.log('[Verify] parsed deepToken =', deepToken);
      if (!deepToken || deepToken === 'null') return;
      setBusy(true);
      try {
        console.log('[Verify] Signing in with customToken');
        const auth = getAuth();
        await signInWithCustomToken(auth, deepToken);
        await refreshBackendUser();
        navigate('/app/onboarding', { replace: true });
      } catch (err) {
        console.error('[Verify] Deep link sign-in error', err);
        setStatus('error');
      } finally {
        setBusy(false);
      }
    };

    App.addListener('appUrlOpen', handleDeepLink);
    if (customTokenParam && customTokenParam !== 'null') {
      handleDeepLink({ url: `panpals://verify?customToken=${customTokenParam}` });
    }
    // cleanup must be sync—don’t return the promise
    return () => {
      void App.removeAllListeners();
    };
  }, [customTokenParam, navigate, refreshBackendUser]);

  // 2) Watch backendUser.isEmailVerified on native and auto-route
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'web' && backendUser?.isEmailVerified) {
      console.log('[Verify] backendUser is verified → routing to onboarding');
      navigate('/app/onboarding', { replace: true });
    }
  }, [backendUser, navigate]);

  // 2) Send verification email once (skip if we're here via any deep-link)
  useEffect(() => {
    if (
      loading ||
      token ||
      customTokenParam ||
      sentEmail.current ||
      backendUser?.isEmailVerified    // skip if they’re already verified
    ) {
      return;
    }
    sentEmail.current = true;
    setBusy(true);
    fetchWithAuth('/api/email/verify/start', { method: 'POST' })
      .then(() => setStatus('sent'))
      .catch((err: any) => {
        console.error('[Verify] Error sending verification email:', err);
        if (err.status === 429 || err.message.includes('less than an hour ago')) {
          setStatus('sent');
        } else {
          setStatus('error');
        }
      })
      .finally(() => setBusy(false));
  }, [loading, token, customTokenParam]);

  // 3) Confirm email token — **web only**, native apps will pick up the customToken via deep‐link
  useEffect(() => {
    if (!token) return;
    setBusy(true);
    (async () => {
      try {
        const res = await fetch(
          `${apiConfig.apiBaseUrl}/api/email/verify/confirm`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          }
        );
        if (!res.ok) throw new Error('Confirm failed');
        const { customToken } = await res.json();
        console.log('[Verify] 🔄 confirm returned customToken=', customToken);
        setWebCustomToken(customToken);
        setStatus('success');

        // immediately hand off into the native app…
        window.location.href = `panpals://verify?customToken=${customToken}`;
      } catch (err) {
        console.error('[Verify] Confirmation error:', err);
        setStatus('error');
      } finally {
        setBusy(false);
      }
    })();
  }, [token]);

  // 4) Remaining effects (retry timer & render logic) unchanged
  useEffect(() => {
    if (status !== 'sent' && status !== 'error' && status !== 'resent') return;
    (async () => {
      try {
        const res = await fetchWithAuth('/api/email/verify/status');
        const { lastSentAt } = res;
        if (!lastSentAt) return setRetrySeconds(null);
        const elapsed = Math.floor((Date.now() - new Date(lastSentAt).getTime()) / 1000);
        const remaining = 3600 - elapsed;
        setRetrySeconds(remaining > 0 ? remaining : null);
      } catch {
        // ignore
      }
    })();
  }, [status]);

  useEffect(() => {
    if (retrySeconds === null) return;
    const timer = setInterval(() => {
      setRetrySeconds((prev) => (prev && prev > 1 ? prev - 1 : null));
    }, 1000);
    return () => clearInterval(timer);
  }, [retrySeconds]);

  useEffect(() => {
    // only on native, once back-end says "isEmailVerified: true", fire off to onboarding
    if (Capacitor.getPlatform() !== 'web' && backendUser?.isEmailVerified) {
      navigate('/app/onboarding', { replace: true });
    }
  }, [backendUser, navigate]);

  const handleResend = async () => {
    setBusy(true);
    try {
      await fetchWithAuth('/api/email/verify/start', { method: 'POST' });
      setStatus('resent');
      setRetrySeconds(null);
    } catch (err: any) {
      if (err.status === 429 || err.message.includes('less than an hour ago')) {
        setRetrySeconds(3600);
      } else {
        setStatus('error');
      }
    } finally {
      setBusy(false);
    }
  };


  const ResendBlock = () => (
    <>
      <Button
        variant="black"
        className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
        disabled={busy || retrySeconds !== null}
        onClick={handleResend}
      >
        {busy ? 'Sending…' : 'Resend verification email'}
      </Button>
      {retrySeconds !== null && (
        <p className="text-sm text-gray-500 mt-2">
          You can request a new link in{' '}
          {Math.floor(retrySeconds / 60)}:
          {(retrySeconds % 60).toString().padStart(2, '0')}
        </p>
      )}
    </>
  );

  const renderMessage = () => {
    // console.log('[Verify] Rendering message, status:', status, 'backendUser:', backendUser);
    switch (status) {
      case 'sent':
        return (
          <div className="text-center space-y-3">
            <p className="text-blue-600 font-semibold text-lg">
              Verification email sent
            </p>
            <p className="text-gray-600">
              Check your inbox and click the link we emailed you
            </p>
            <ResendBlock />
          </div>
        );
      case 'resent':
        return (
          <div className="text-center space-y-3">
            <p className="text-green-600 font-semibold text-lg">
              We sent a new verification email
            </p>
            <ResendBlock />
          </div>
        );
      case 'error':
        return (
          <div className="text-center space-y-3">
            <p className="text-red-600 font-semibold text-lg">
              ❌ Invalid or expired link
            </p>
            <ResendBlock />
          </div>
        );
      case 'pending':
        return (
          <p className="text-gray-600 text-center text-lg">
            Verifying your email…
          </p>
        );
      case 'success':
        return (
          <div className="text-center space-y-3">
            <p className="text-green-600 font-semibold text-lg">
              Email verified!
            </p>
            <p className="text-gray-600">Redirecting you to the app…</p>
            {Capacitor.getPlatform() === 'web' && webCustomToken ? (
              <a
                href={`panpals://verify?customToken=${webCustomToken}`}
                className="underline mt-2 inline-block"
                onClick={() =>
                  console.log(
                    '[Verify] 🔗 clicking deep-link →',
                    `panpals://verify?customToken=${webCustomToken}`
                  )
                }
              >
                Continue in the app
              </a>
            ) : null}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="background-light-primary min-h-screen flex flex-col items-center justify-start relative">
      <img
        src={status === 'success' ? '/logo-verified.png' : '/logo-waiting.png'}
        alt="PanPals Logo"
        className="w-full h-auto z-10"
      />
      <div className="bg-white shadow-xl w-full px-6 pt-10 pb-4 z-20">
        {renderMessage()}
        <Button
          variant="outline"
          className="block mx-auto mt-4 w-50 py-2"
          onClick={() => navigate('/auth')}
        >
          Back to Sign Up
        </Button>
      </div>
    </div>
  );
}